/**
 * NotificationsContext - Context per gestire le notifiche real-time in modo centralizzato
 * Condiviso tra NotificationBell e pagina Notifications
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUser } from '../redux/main/authSlice';
import { 
    initializeSocket, 
    onNotification,
    markNotificationRead as socketMarkRead,
    markAllNotificationsRead as socketMarkAllRead 
} from '../services/socketService';

const NotificationsContext = createContext();

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

export const NotificationsProvider = ({ children }) => {
    const user = useSelector(selectUser);
    
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isFlashing, setIsFlashing] = useState(false);
    const [flashCount, setFlashCount] = useState(0);
    const [isConnected, setIsConnected] = useState(false);
    
    // Inizializza Socket.IO quando l'utente è autenticato
    useEffect(() => {
        if (user && user.accountId) {
            initializeSocket({
                accountId: user.accountId,
                userId: user._id,
                userName: user.name || user.email
            });
            
            setIsConnected(true);
            
            // Registra callback per nuove notifiche
            const unsubscribe = onNotification((notification) => {
                // Aggiungi la notifica in cima alla lista
                setNotifications(prev => {
                    const newList = [notification, ...prev];
                    return newList;
                });
                
                // Incrementa contatore non lette
                setUnreadCount(prev => prev + 1);
                
                // Avvia lampeggio rosso
                startFlashing();
            });
            
            return () => {
                unsubscribe();
                setIsConnected(false);
            };
        }
    }, [user]);
    
    // Gestione lampeggio
    const startFlashing = useCallback(() => {
        setIsFlashing(true);
        setFlashCount(0);
    }, []);
    
    const stopFlashing = useCallback(() => {
        setIsFlashing(false);
        setFlashCount(0);
    }, []);
    
    useEffect(() => {
        if (isFlashing && flashCount < 10) {
            const timer = setTimeout(() => {
                setFlashCount(prev => prev + 1);
            }, 300);
            
            return () => clearTimeout(timer);
        } else if (flashCount >= 10) {
            setIsFlashing(false);
            setFlashCount(0);
        }
    }, [isFlashing, flashCount]);
    
    // Marca una notifica come letta
    const markAsRead = useCallback((notificationId) => {
        socketMarkRead(notificationId);
        setNotifications(prev => 
            prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);
    
    // Marca tutte come lette
    const markAllAsRead = useCallback(() => {
        socketMarkAllRead();
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setUnreadCount(0);
    }, []);
    
    // Elimina una notifica
    const deleteNotification = useCallback((notificationId) => {
        setNotifications(prev => {
            const notification = prev.find(n => n.id === notificationId);
            if (notification && !notification.read) {
                setUnreadCount(count => Math.max(0, count - 1));
            }
            return prev.filter(n => n.id !== notificationId);
        });
    }, []);
    
    // Elimina tutte le notifiche
    const deleteAllNotifications = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
    }, []);
    
    const value = {
        notifications,
        unreadCount,
        isFlashing,
        flashCount,
        isConnected,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
        stopFlashing
    };
    
    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
};

export default NotificationsContext;
